import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { startOfWeek, endOfWeek, format, subWeeks, differenceInDays, startOfDay } from "date-fns";

export interface WeeklyProgress {
  weekLabel: string;
  totalMinutes: number;
  goalMinutes: number;
  achievementPercentage: number;
  startDate: Date;
  endDate: Date;
}

export interface StudyPattern {
  dayOfWeek: string;
  averageMinutes: number;
  sessionCount: number;
}

export interface TopicProgress {
  topicId: string;
  topicTitle: string;
  weeklyMinutes: number;
  goalMinutes: number;
  achievementPercentage: number;
  streak: number;
  totalSessions: number;
}

export interface StudyAnalytics {
  weeklyProgress: WeeklyProgress[];
  studyPatterns: StudyPattern[];
  topicProgress: TopicProgress[];
  overallStats: {
    totalStudyTime: number;
    averageSessionDuration: number;
    currentStreak: number;
    goalsAchieved: number;
    totalGoals: number;
  };
}

// Hook for weekly progress calculations
export const useWeeklyProgress = (weeksCount = 4) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['weekly-progress', user?.id, weeksCount],
    queryFn: async (): Promise<WeeklyProgress[]> => {
      if (!user) return [];

      const weeks: WeeklyProgress[] = [];
      const currentDate = new Date();

      for (let i = 0; i < weeksCount; i++) {
        const weekStart = startOfWeek(subWeeks(currentDate, i));
        const weekEnd = endOfWeek(subWeeks(currentDate, i));

        // Fetch study sessions for this week
        const { data: sessions, error } = await supabase
          .from('study_time_sessions')
          .select('duration_seconds, topic_id')
          .eq('user_id', user.id)
          .gte('start_time', weekStart.toISOString())
          .lte('start_time', weekEnd.toISOString());

        if (error) {
          console.error('Error fetching weekly sessions:', error);
          continue;
        }

        const totalMinutes = sessions?.reduce((sum, session) => 
          sum + Math.floor(session.duration_seconds / 60), 0) || 0;

        // Get unique topic IDs and fetch their goals
        const topicIds = [...new Set(sessions?.map(s => s.topic_id) || [])];
        let goalMinutes = 0;

        if (topicIds.length > 0) {
          const { data: topics } = await supabase
            .from('topics')
            .select('study_goal_hours_per_week')
            .in('id', topicIds)
            .eq('user_id', user.id);

          goalMinutes = topics?.reduce((sum, topic) => 
            sum + ((topic.study_goal_hours_per_week || 0) * 60), 0) || 0;
        }

        weeks.push({
          weekLabel: i === 0 ? 'This Week' : `${i} week${i > 1 ? 's' : ''} ago`,
          totalMinutes,
          goalMinutes,
          achievementPercentage: goalMinutes > 0 ? Math.round((totalMinutes / goalMinutes) * 100) : 0,
          startDate: weekStart,
          endDate: weekEnd
        });
      }

      return weeks.reverse(); // Show oldest to newest
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook for study patterns (day of week analysis)
export const useStudyPatterns = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['study-patterns', user?.id],
    queryFn: async (): Promise<StudyPattern[]> => {
      if (!user) return [];

      // Get last 4 weeks of data
      const fourWeeksAgo = startOfWeek(subWeeks(new Date(), 4));

      const { data: sessions, error } = await supabase
        .from('study_time_sessions')
        .select('start_time, duration_seconds')
        .eq('user_id', user.id)
        .gte('start_time', fourWeeksAgo.toISOString());

      if (error) {
        console.error('Error fetching study patterns:', error);
        return [];
      }

      const dayPatterns: { [key: string]: { totalMinutes: number; sessionCount: number } } = {
        'Sunday': { totalMinutes: 0, sessionCount: 0 },
        'Monday': { totalMinutes: 0, sessionCount: 0 },
        'Tuesday': { totalMinutes: 0, sessionCount: 0 },
        'Wednesday': { totalMinutes: 0, sessionCount: 0 },
        'Thursday': { totalMinutes: 0, sessionCount: 0 },
        'Friday': { totalMinutes: 0, sessionCount: 0 },
        'Saturday': { totalMinutes: 0, sessionCount: 0 }
      };

      sessions?.forEach(session => {
        const dayName = format(new Date(session.start_time), 'EEEE');
        const minutes = Math.floor(session.duration_seconds / 60);
        
        dayPatterns[dayName].totalMinutes += minutes;
        dayPatterns[dayName].sessionCount += 1;
      });

      return Object.entries(dayPatterns).map(([dayOfWeek, data]) => ({
        dayOfWeek,
        averageMinutes: data.sessionCount > 0 ? Math.round(data.totalMinutes / 4) : 0, // Average per week
        sessionCount: data.sessionCount
      }));
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook for topic-specific progress
export const useTopicProgress = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['topic-progress', user?.id],
    queryFn: async (): Promise<TopicProgress[]> => {
      if (!user) return [];

      const currentWeekStart = startOfWeek(new Date());
      const currentWeekEnd = endOfWeek(new Date());

      // Get all topics with their goals
      const { data: topics, error: topicsError } = await supabase
        .from('topics')
        .select('id, title, study_goal_hours_per_week')
        .eq('user_id', user.id);

      if (topicsError) {
        console.error('Error fetching topics:', topicsError);
        return [];
      }

      const progress: TopicProgress[] = [];

      for (const topic of topics || []) {
        // Get this week's study time for this topic
        const { data: weekSessions, error: weekError } = await supabase
          .from('study_time_sessions')
          .select('duration_seconds')
          .eq('user_id', user.id)
          .eq('topic_id', topic.id)
          .gte('start_time', currentWeekStart.toISOString())
          .lte('start_time', currentWeekEnd.toISOString());

        if (weekError) {
          console.error('Error fetching week sessions:', weekError);
          continue;
        }

        const weeklyMinutes = weekSessions?.reduce((sum, session) => 
          sum + Math.floor(session.duration_seconds / 60), 0) || 0;

        const goalMinutes = (topic.study_goal_hours_per_week || 0) * 60;

        // Calculate streak (consecutive days with study activity)
        let streak = 0;
        const today = new Date();
        
        for (let i = 0; i < 30; i++) { // Check last 30 days
          const checkDate = subWeeks(today, 0);
          checkDate.setDate(checkDate.getDate() - i);
          
          const dayStart = startOfDay(checkDate);
          const dayEnd = new Date(dayStart);
          dayEnd.setHours(23, 59, 59, 999);

          const { data: daySession } = await supabase
            .from('study_time_sessions')
            .select('id')
            .eq('user_id', user.id)
            .eq('topic_id', topic.id)
            .gte('start_time', dayStart.toISOString())
            .lte('start_time', dayEnd.toISOString())
            .limit(1);

          if (daySession && daySession.length > 0) {
            streak = i + 1;
          } else if (i === 0) {
            // If no activity today, streak is 0
            break;
          } else {
            // Found a gap, stop counting
            break;
          }
        }

        // Get total session count for this topic
        const { data: allSessions } = await supabase
          .from('study_time_sessions')
          .select('id')
          .eq('user_id', user.id)
          .eq('topic_id', topic.id);

        progress.push({
          topicId: topic.id,
          topicTitle: topic.title,
          weeklyMinutes,
          goalMinutes,
          achievementPercentage: goalMinutes > 0 ? Math.round((weeklyMinutes / goalMinutes) * 100) : 0,
          streak,
          totalSessions: allSessions?.length || 0
        });
      }

      return progress.sort((a, b) => b.achievementPercentage - a.achievementPercentage);
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Main analytics hook that combines everything
export const useStudyAnalytics = (): { data: StudyAnalytics | undefined; isLoading: boolean } => {
  const { data: weeklyProgress, isLoading: weeklyLoading } = useWeeklyProgress();
  const { data: studyPatterns, isLoading: patternsLoading } = useStudyPatterns();
  const { data: topicProgress, isLoading: topicLoading } = useTopicProgress();
  const { user } = useAuth();

  const overallStatsQuery = useQuery({
    queryKey: ['overall-stats', user?.id],
    queryFn: async () => {
      if (!user) return null;

      // Get total study time
      const { data: allSessions } = await supabase
        .from('study_time_sessions')
        .select('duration_seconds')
        .eq('user_id', user.id);

      const totalStudyTime = allSessions?.reduce((sum, session) => 
        sum + Math.floor(session.duration_seconds / 60), 0) || 0;

      const averageSessionDuration = allSessions?.length ? 
        Math.round(totalStudyTime / allSessions.length) : 0;

      // Calculate current streak (consecutive days with any study activity)
      let currentStreak = 0;
      const today = new Date();
      
      for (let i = 0; i < 30; i++) {
        const checkDate = new Date(today);
        checkDate.setDate(checkDate.getDate() - i);
        
        const dayStart = startOfDay(checkDate);
        const dayEnd = new Date(dayStart);
        dayEnd.setHours(23, 59, 59, 999);

        const { data: daySession } = await supabase
          .from('study_time_sessions')
          .select('id')
          .eq('user_id', user.id)
          .gte('start_time', dayStart.toISOString())
          .lte('start_time', dayEnd.toISOString())
          .limit(1);

        if (daySession && daySession.length > 0) {
          currentStreak = i + 1;
        } else if (i === 0) {
          break;
        } else {
          break;
        }
      }

      return {
        totalStudyTime,
        averageSessionDuration,
        currentStreak,
        goalsAchieved: topicProgress?.filter(t => t.achievementPercentage >= 100).length || 0,
        totalGoals: topicProgress?.length || 0
      };
    },
    enabled: !!user && !!topicProgress,
  });

  const isLoading = weeklyLoading || patternsLoading || topicLoading || overallStatsQuery.isLoading;

  if (isLoading || !weeklyProgress || !studyPatterns || !topicProgress || !overallStatsQuery.data) {
    return { data: undefined, isLoading };
  }

  return {
    data: {
      weeklyProgress,
      studyPatterns,
      topicProgress,
      overallStats: overallStatsQuery.data
    },
    isLoading: false
  };
};